import React from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    List,
    ListItem,
    Link as MuiLink // Import MuiLink for consistent styling
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqData = [
    {
        question: "Why is it called 'Radian'?",
        answer: "In mathematical terms, a radian is a unit of angular measure (57.296° if you want to be precise). Just as multiple radians comprise a full circle, Radian gives you the opportunity to gather perspectives about yourself from multiple angles, reflecting the 360-degree nature of its suite of feedback tools. This allows you to see yourself from all sides."
    },
    {
        question: "Why should I use Radian?",
        answer: (
            <Box>
                <List dense sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
                    <ListItem sx={{ display: 'list-item', p: 0}}><Typography variant="body1" color="text.secondary">For You, Not HR: This is your data, for your development. Radian is built for the individual, not for your boss's performance review file.</Typography></ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0}}><Typography variant="body1" color="text.secondary">Holistic & Evidence-Based: Radian integrates validated models of communication, personality, and reputation into a single, cohesive platform.</Typography></ListItem>
                    <ListItem sx={{ display: 'list-item', p: 0}}><Typography variant="body1" color="text.secondary">Actionable Insights: It's not just data; it's a roadmap. Radian provides concrete feedback and AI-powered recommendations—including your own Brand Clarity strategy—to help you refine your brand and build a more powerful reputation.</Typography></ListItem>
                </List>
            </Box>
        )
    },
    {
        question: "Is Radian an HR tool?",
        answer: (
            <Box>
                <Typography paragraph>
                    The short answer is no.  Radian is intended to provide tools for any professional to build their own self-awareness and to use evidence to craft and refine their personal professional brands absent any organizational context.
                </Typography>
                <Typography paragraph>
                    While some pieces of the Radian suite are based on tools used throughout industry for career pathing, hiring, termination, and succession planning decisions, Radian is committed to helping individual professionals exceed their potential, not helping human resources teams manage risk or people.
                </Typography>
                <Typography>
                    That said, some companies choose to invest in Radian access to support their employees' professional growth.  In such circumstances, the employees still remain the only people in their organization with access to the robust Radian reports.
                </Typography>
            </Box>
        )
    },
    {
        question: "What is reputation?",
        answer: (
            <Box>
                <Typography paragraph>
                    Reputation is the collective perception others have of your abilities, character, and contributions over time. Your reputation is defined by what others think of you, how they feel about you, and how they behave around and toward you.
                </Typography>
                <Typography paragraph>
                    Your reputation may change over time, but it is the single largest determinant of your ability to accomplish your goals. Regardless of your knowledge or technical skills (which may inform it), your reputation truly defines you.
                </Typography>
                <Typography paragraph>
                    Individual reputation characteristics may be divided into two categories, Inputs that influence what others think and how they feel about you, and Outcomes that represent how others behave around and toward you.
                </Typography>
                <Typography paragraph>
                    Reputation inputs - the behaviors and habits you exhibit that influence how other think and feel about you - may include your professionalism, your conduct, your citizenship, your competence, your dependability, your creativity, and your treatment of others.
                </Typography>
                <Typography paragraph>
                    Reputation outcomes - the behaviors of others toward you that are influenced by your reputation inputs - may include whether they will recommend you to others, whether they will rely on you, whether they will want to work with or hire you, whether they trust you, whether they will give you the benefit of the doubt, whether they will want to spend time with you, and whether they will say positive things about you.
                </Typography>
            </Box>
        )
    },
    {
        question: "How is reputation different from identity?",
        answer: "Identity is your internal sense of self, while reputation is the external perception of you held by others. Some people have an accurate sense of how they are perceived by others, but all of us benefit from having evidence of how well we understand our reputation and how well it aligns with our identity. Radian helps you understand and align these two. The more closely your reputation aligns with your identity - your core sense of who you are - the more authentic you are and the more you will seem credible to others."
    },
    {
        question: "How do identity and reputation relate to my brand?",
        answer: "Your professional brand is the intentional projection of your identity, and your reputation is the measure of how successfully that brand is being received. Your brand is the way you communicate with others to influence what they think and how they feel about you so, which in turn will affect how they behave toward and around you."
    },
    {
        question: "What is 360° evaluation?",
        answer: "A popular tool for leadership assessments and career pathing, 360° evaluations are a process for gathering anonymous feedback from peers, direct reports, and supervisors to get a well-rounded view of your professional conduct and performance. Radian's 360° evaluations focus on your close connections' personal experiences of your behavior and provide you with anonymous feedback about your contributions to your work team's professional successes."
    },
    {
        question: "What is the Self-Perception Inventory?",
        answer: (
            <Box>
                <Typography paragraph>
                    The Self-Perception Inventory (SPI) is a tool for self-reflection, helping you define and understand your own attitudinal communication style, work environment preferences, preferred informal team roles, and core motivators and values.
                </Typography>
                <Typography paragraph>
                    All parts of the SPI are based on evidence-based theories and assessment tools from Robert Merrill and Roger Reid (Social Styles), Robert and Dorothy Bolton ('PeopleStyles at Work'), Erin Meyer ('The Culture Map'), and Hogan Assessment Systems.
                </Typography>
                <Typography>
                    The Radian model builds on these sources by unifying them within the think-feel-do framework of attitudes as a precursor to action.  Radian also provides users with the capacity to compare their own self-perception with the perceptions of them by those in their immediate professional networks.
                </Typography>
            </Box>
        )
    },
    {
        question: "What is the Professional Reputation Survey?",
        answer: "The Professional Reputation Survey (PRS) is a user-driven, anonymous survey tool to gather external feedback on your reputation from a curated list of reviewers. Once configured, respondents to your PRS will not need a Radian account to access the survey and provide you with the anonymous feedback you can use to refine your brand and bring your reputation into closer alignment with your identity. The PRS may be administered on its own, but it is most effective when used in conjunction with the SPI. The PRS covers your behavioral communication style, reputational descriptors, and communication methods they use with you. The PRS also provides respondents the opportunity to provide free-response feedback that can add nuance and insight to your understanding of your reputation."
    },
    {
        question: "What is the Charisma Diagnostic?",
        answer: "The Charisma Diagnostic (CD) is tool for assessing your balance of warmth and confidence, key components of charismatic presence. This version of the CD is based on a similar assessment of the same name by Vanessa Van Edwards ('Cues'). The description of charisma as the intersection of warmth and confidence is well documented. After completing the CD, Radian provides you with tips for boosting both your warmth and confidence cues, or the signals you send to others that indicate your respective levels of both."
    },
    {
        question: "How are the various assessment results calculated?",
        answer: "Each assessment in Radian is designed to provide meaningful insights based on established, evidence-based models of communication and professional behavior.  Self-assessments, like the Self-Perception Inventory, analyze your direct responses to map your personal communication style and workplace preferences. Feedback-based tools, such as the 360° Evaluation and Professional Reputation Survey, calculate your results by aggregating anonymous responses from your colleagues to identify common themes and create an average of how you are perceived by others."
    },
    {
        question: "Is my data secure and private?",
        answer:  (
            <Box>
                <Typography paragraph>
                    Yes, protecting your data is our highest priority. We have implemented multiple layers of security and privacy controls to ensure your information is handled responsibly and confidentially. Here’s a breakdown of our approach:
                </Typography>
                <Typography paragraph>
                    1. Account Security
                </Typography>
                <Typography paragraph>
                    Password Encryption: Your password is never stored in plain text. We use a strong, one-way cryptographic hashing algorithm (bcrypt) to protect your credentials. This means that even in the unlikely event of a database breach, your password would remain encrypted and unreadable.
                </Typography>
                <Typography paragraph>
                    Secure Authentication: Your session is protected using industry-standard JSON Web Tokens (JWTs). Every request you make to our servers is authenticated to ensure that only you can access your account data.
                </Typography>
                <Typography paragraph>
                    2. Data & System Security
                </Typography>
                <Typography paragraph>
                    Data Encryption in Transit: All communication between your web browser and our servers is encrypted using HTTPS with Transport Layer Security (TLS). This prevents eavesdropping and ensures that your data cannot be intercepted as it travels over the internet.
                </Typography>
                <Typography paragraph>
                    Secure Infrastructure: Radian is built on a modern, secure cloud infrastructure. Our database and application servers are hosted with a leading cloud provider, benefiting from their extensive physical and network security measures.
                </Typography>
                <Typography paragraph>
                    Secret Management: Sensitive credentials, such as database passwords and API keys, are stored in a secure, managed secret vault, not in our codebase, to minimize risk.
                </Typography>
                <Typography paragraph>
                    3. Anonymity and Privacy by Design
                </Typography>
                <Typography paragraph>
                    360° Evaluation & Professional Reputation Survey (PRS): We understand that honest feedback depends on confidentiality.  Responses you provide for others in a 360° Evaluation are aggregated and presented anonymously. Your name is never directly attached to the qualitative or quantitative feedback you give.  Similarly, when others respond to your Professional Reputation Survey, their identities are kept confidential to ensure they can provide candid feedback.
                </Typography>
                <Typography paragraph>
                    Self-Perception Inventory (SPI) and Charisma Diagnostic (CD): These are self-assessments. Your individual responses and results are private to your account. While administrators or instructors may have access to aggregated, anonymized cohort or team-level data (e.g., "the team's average score on Context is 2.5"), they cannot view your specific individual answers unless you choose to share your report.
                </Typography>
                <Typography paragraph>
                    Team Reports: For features like the Team SPI Report, insights are only generated when a minimum number of team members (e.g., three) have completed the assessment. This prevents individuals from being easily identified in a small group.
                </Typography>
                <Typography paragraph>
                    4. Access Control
                </Typography>
                <Typography paragraph>
                    Role-Based Permissions: Radian has a strict role-based access control system:
                </Typography>
                <Typography paragraph>
                    Users can only see their own data and reports.
                </Typography>
                <Typography paragraph>
                    Moderators (e.g., Instructors, Managers) can view data and reports for the specific cohorts or teams they are assigned to, but typically only in an aggregated, anonymized format.
                </Typography>
                <Typography paragraph>
                    Administrators have broader access for system management but are bound by our privacy policy. The "view as" feature, for example, is a tool for support and troubleshooting and is logged.
                </Typography>
                <Typography paragraph>
                    We are committed to maintaining the security and privacy of your data. If you have any further questions or concerns, please do not hesitate to <MuiLink href="mailto:ohhello@radian.ooo">contact us</MuiLink>.
                </Typography>
            </Box>
        )
    },
    {
        question: "What do I do if something goes wrong with Radian?",
        answer: (
            <Box>
                <Typography paragraph>
                    Radian is a relatively new platform, so it is possible that things will break or not work as intended. If something goes wrong, please first try a hard refresh (press Ctrl-Shift-R on a Windows computer or Command-Shift-R on a Mac).
                </Typography>
                <Typography paragraph>
                    If that does not resolve the problem, try a different browser, accessing Radian from an incognito browser, or clearing the browser's cache.
                </Typography>
                <Typography>
                    If these suggestions fail, please contact Radian at <MuiLink href="mailto:ohhello@radian.ooo">ohhello@radian.ooo</MuiLink> and we'll get back to you as soon as we can.
                </Typography>
            </Box>
        )
    },
    {
        question: "Who is behind Radian?",
        answer: (
             <Box>
                <Typography paragraph>
                    Radian is the creation of Michael Mandel. By day, Michael is a Senior Lecturer in Business Communication at the University of Arizona's <MuiLink href="https://eller.arizona.edu/" target="_blank" rel="noopener noreferrer">Eller College of Management</MuiLink>. In that role, he initially developed, compiled, or refined many of the assessments that now comprise the Radian platform.
                </Typography>
                <Typography paragraph>
                    Michael is a a certified <MuiLink href="https://www.hoganassessments.com/" target="_blank" rel="noopener noreferrer">Hogan Assessments</MuiLink> coach and has been recognized for his teaching and service work. Michael has been a consultant in communication and management for two decades through his boutique practice, <MuiLink href="https://www.m2powered.com" target="_blank" rel="noopener noreferrer">Mandel Management Solutions</MuiLink>.
                </Typography>
                <Typography>
                    Michael 'vibe-coded' Radian - mostly with Google Gemini and a touch of ChatGPT - entirely on his own time, using his own equipment, and out of his own pocket for the backend hosting. Please visit his <MuiLink href="https://www.m2powered.com" target="_blank" rel="noopener noreferrer">other website</MuiLink> for ways you can support this effort.
                </Typography>
            </Box>
        )
    }
];

function AboutPage() {
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
                <Typography variant="h3" component="h1" align="center" gutterBottom>
                    About Radian
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" paragraph>
                    Understanding Your Professional Reputation
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ my: 3 }}>
                    <Typography variant="body1" paragraph sx={{textAlign: 'left'}}>
                        Welcome to Radian, a platform designed to help you gain a deeper understanding of your professional identity and reputation. Through a suite of evidence-based assessments, Radian provides a holistic view of how you perceive yourself and how you are perceived by others.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{textAlign: 'left'}}>
                        Our goal is not just professional development in a single role, but your personal and professional growth beyond any one context. By bringing your actual reputation into closer alignment with your identity, you can build a more authentic and impactful personal brand.
                    </Typography>
                </Box>
                
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ mt: 4 }}>
                    Frequently Asked Questions
                </Typography>
                <Divider sx={{ my: 3 }} />

                <Box>
                    {faqData.map((faq, index) => (
                        <Accordion key={index}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}a-content`}
                                id={`panel${index}a-header`}
                            >
                                <Typography variant="h6" component="h3">{faq.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography sx={{textAlign: 'left'}}>
                                    {faq.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Paper>
        </Container>
    );
}

export default AboutPage;
